import React, { useEffect, useState } from 'react';
import { Sidebar } from './Components/Catalyst/sidebar';
import {
  HomeIcon,
  BanknotesIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  ArrowRightStartOnRectangleIcon,
  BellIcon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  MoonIcon,
} from '@heroicons/react/16/solid';
import logo from './assets/epic-hoas-icon.png';
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from './Components/Catalyst/dropdown';
import { Avatar } from './Components/Catalyst/avatar';
import { Switch } from './Components/Catalyst/switch';
import {
  clearAllTokens,
  clearRememberMe,
  clearSessionStorage,
  clearUserName,
} from './utils/TokenUtils';
import { useNavigate } from 'react-router-dom';
import { memberAuthSessionService } from './clients/ApiClients';
import { memberNotificationService } from './clients/ApiClients';
import { useMember } from './Hooks/MemberContext';
import { useTheme } from './Components/Custom/ThemeProvider';
import { NavIconLink, NavItem, NavSection } from './Components/Shell/NavPrimitives';
import { useCommandPalette } from './Components/Shell/CommandPaletteProvider';
import { paletteShortcutLabel } from './Components/Shell/CommandPalette';

interface AppsidebarProps {
  currentPage: string;
}

/**
 * Linear-style sidebar (#603 step 3), the homeowner's version: the same
 * shape as front-porch's — header menu, dense items, collapsible sections
 * — with the member's own homes where a manager sees their associations.
 */
const AppSidebar: React.FC<AppsidebarProps> = ({ currentPage }) => {
  const navigate = useNavigate();
  const { isDarkMode, toggleDarkMode } = useTheme();
  // Name, email and initials come from `GET /member-portal/me` via the
  // context — the member JWT carries none of them.
  const { member, displayName, initials, homes, selectedHome, selectHome } =
    useMember();
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const { openPalette } = useCommandPalette();

  // Fetch unread notifications count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        // `total` is the count of everything matching the filter, not the
        // size of this page — asking for one row is enough to read it.
        const response = await memberNotificationService.listMyNotifications({
          unreadOnly: true,
          perPage: 1,
        });
        setUnreadCount(response.data.total);
      } catch (error) {
        console.error('Failed to fetch unread notifications count:', error);
        // Don't show error to user, just keep count at 0
      }
    };

    fetchUnreadCount();

    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    // Tell the server first — clearing local storage only makes this
    // browser forget the token, it does not stop the token working.

    // Set when the session came from WorkOS. WorkOS keeps a session of
    // its own that only the browser can end, so ending ours is half the
    // job: without this the next sign-in goes straight through with no
    // prompt, which is not what anyone means by "sign out".
    let workosLogoutUrl: string | undefined;

    try {
      // Bounded so a hung or unreachable server cannot leave someone
      // stuck on a screen they asked to leave. Clearing locally below is
      // not conditional on this succeeding.
      const response = await memberAuthSessionService.memberLogout({
        timeout: 5000,
      });
      workosLogoutUrl = response.data?.workos_logout_url ?? undefined;
    } catch (err) {
      // Sign-out must never leave someone stuck in the app. A 401 here
      // just means the session was already gone.
      console.error('Server sign-out failed; clearing locally anyway', err);
    }
    clearAllTokens();
    clearUserName();
    clearRememberMe();
    clearSessionStorage();

    if (workosLogoutUrl) {
      // Leaves the app. WorkOS ends its session and sends the browser
      // back to the sign-in page itself, so there is no navigate() to
      // race with — and `replace` keeps the signed-in page out of
      // history.
      window.location.replace(workosLogoutUrl);
      return;
    }

    navigate('/login');
  };

  const unreadBadge =
    unreadCount > 0 ? (
      <span
        className="ml-auto rounded-full bg-canvas-fg-strong/15 px-1.5 text-[11px] font-semibold leading-4 text-canvas-fg-strong"
        aria-label={`${unreadCount} unread`}
      >
        {unreadCount > 99 ? '99+' : unreadCount}
      </span>
    ) : undefined;

  return (
    <Sidebar className="bg-canvas">
      {/* Header: the association this member's selected home is in, and
          the account menu. */}
      <div className="flex items-center gap-1 px-3 pb-2 pt-3">
        <Dropdown>
          <DropdownButton
            plain
            className="!min-w-0 flex-1 !justify-start !px-1.5 !py-1 !text-canvas-fg-strong hover:!bg-canvas-fg-strong/10"
            aria-label="Account menu"
          >
            <img src={logo} alt="" className="h-5 w-auto" />
            <span className="min-w-0 truncate text-[13px] font-semibold">
              {selectedHome?.hoa_name ?? 'Epic HOAs'}
            </span>
            <ChevronDownIcon className="h-3.5 w-3.5 shrink-0 !fill-canvas-fg" />
          </DropdownButton>
          <DropdownMenu anchor="bottom start" className="min-w-56">
            <DropdownItem href="/settings">
              <Cog6ToothIcon />
              <DropdownLabel>Settings</DropdownLabel>
            </DropdownItem>
            <DropdownDivider />
            {/* No `href` — signing out is an action, and `/logout` was
                never a route. It fell through to `*` and redirected to
                /login, which happened to look right. */}
            <DropdownItem onClick={handleLogout}>
              <ArrowRightStartOnRectangleIcon />
              <DropdownLabel>Sign out</DropdownLabel>
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
        <button
          type="button"
          onClick={openPalette}
          aria-label="Search or jump to"
          aria-keyshortcuts="Meta+K Control+K"
          title={`Search (${paletteShortcutLabel()})`}
          className="rounded-md p-1.5 hover:bg-canvas-fg-strong/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-canvas-fg-strong/60"
        >
          <MagnifyingGlassIcon className="h-4 w-4 text-canvas-fg" aria-hidden />
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-3 pb-3">
        <NavSection id="primary">
          <NavItem to="/home" icon={HomeIcon} current={currentPage === 'home'}>
            Home
          </NavItem>
          <NavItem to="/account" icon={BanknotesIcon} current={currentPage === 'account'}>
            Account
          </NavItem>
          <NavItem
            to="/notifications"
            icon={BellIcon}
            current={currentPage === 'notifications'}
            trailing={unreadBadge}
          >
            Notifications
          </NavItem>
          <NavItem to="/violations" icon={ChartBarIcon} current={currentPage === 'violations'}>
            Violations
          </NavItem>
          <NavItem to="/amenities" icon={BuildingOffice2Icon} current={currentPage === 'amenities'}>
            Amenities
          </NavItem>
          <NavItem to="/reservations" icon={CalendarDaysIcon} current={currentPage === 'reservations'}>
            My reservations
          </NavItem>
        </NavSection>

        {/* A member with one home has nothing to switch between; the
            section appears when there is a choice to make. */}
        {homes.length > 1 && (
          <NavSection id="homes" title="Your homes">
            {homes.map((h) => (
              <NavItem
                key={h.home_id}
                to="/account"
                icon={MapPinIcon}
                current={currentPage === 'account' && selectedHome?.home_id === h.home_id}
                onClick={() => selectHome(h.home_id)}
              >
                {h.street2 ? `${h.street} ${h.street2}` : h.street}
              </NavItem>
            ))}
          </NavSection>
        )}
      </div>

      <div className="mx-3 mb-3 border-t border-canvas-fg-strong/10 pt-2">
        {/* Light / dark as a switch, one click from every page. Labelled
            by aria-label, not an id: on a phone the sidebar renders twice. */}
        <div className="flex items-center justify-between gap-2 px-2 py-1.5">
          <span className="flex items-center gap-2 text-[13px] text-canvas-fg">
            <MoonIcon className="h-4 w-4 fill-canvas-fg" aria-hidden />
            Dark mode
          </span>
          <Switch
            color="sky"
            checked={isDarkMode}
            onChange={() => toggleDarkMode()}
            aria-label="Dark mode"
          />
        </div>
        <div className="mt-1 flex items-center gap-1">
          <div className="flex min-w-0 flex-1 items-center gap-2.5 px-2">
            <Avatar initials={initials} className="size-7 bg-canvas-fg-strong/15 text-canvas-fg-strong" square alt="" />
            <span className="min-w-0">
              <span className="block truncate text-[13px] font-medium text-canvas-fg-strong">
                {displayName || 'Member'}
              </span>
              <span className="block truncate text-[12px] text-canvas-fg">
                {member?.email || 'No email provided'}
              </span>
            </span>
          </div>
          <NavIconLink
            to="/settings"
            icon={Cog6ToothIcon}
            label="Settings"
            current={currentPage === 'settings'}
          />
        </div>
      </div>
    </Sidebar>
  );
};

export default AppSidebar;
